<template>
	<span
		ref="draggedPlaceholder"
		class="draggable-wrapper"
		:style="{ height }">
		<slot />
	</span>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useDraggable } from '@/helpers/dragAndDrop/draggable'

const emit = defineEmits<{
	(e: 'dragover', event: DragEvent): void
	(e: 'dragstart'): void
	(e: 'dragend'): void
}>()

const height = computed(() => {
	if (!draggedPlaceholder.value) return 'unset'
	if (!isDragging.value && !isHovered.value) return 'unset'
	return `${draggedPlaceholder.value.getBoundingClientRect().height}px`
})

const draggedPlaceholder = ref<HTMLElement | null>(null)
const draggedElement = computed(
	() => draggedPlaceholder.value?.firstElementChild as HTMLElement | null,
)
const isDragging = ref<boolean>(false)
const isHovered = ref<boolean>(false)

onMounted(() => {
	if (!draggedPlaceholder.value || !draggedElement.value) return

	useDraggable(draggedElement.value, { onDragStart, onDragStop })
	draggedPlaceholder.value.addEventListener('mouseenter', mouseEnterHandler)
	draggedPlaceholder.value.addEventListener('mouseleave', mouseLeaveHandler)
})

onUnmounted(() => {
	draggedPlaceholder.value?.removeEventListener('mouseenter', mouseEnterHandler)
	draggedPlaceholder.value?.removeEventListener('mouseleave', mouseLeaveHandler)
})

function mouseEnterHandler() {
	isHovered.value = true
}

function mouseLeaveHandler() {
	isHovered.value = false
}

/**
 * Inform parent that dragging has started.
 * Add global style (see CSS below).
 */
function onDragStart() {
	if (!draggedElement.value) return
	emit('dragstart')
	draggedElement.value.classList.add('draggable--dragging')
	document.body.classList.add('dragging')
	isDragging.value = true
}

/**
 * Inform parent that dragging has stopped.
 * Remove global class.
 */
function onDragStop() {
	if (!draggedElement.value) return
	emit('dragend')
	draggedElement.value.classList.remove('draggable--dragging')
	document.body.classList.remove('dragging')
	isDragging.value = false
}
</script>

<style scoped>
.draggable-wrapper {
	display: block;
	width: 100%;
}
.draggable-wrapper {
	pointer-events: initial;
}
.draggable--dragging {
	pointer-events: none;
}
</style>

<style>
body.dragging {
	&,
	* {
		&,
		&:hover,
		&:active {
			cursor: grabbing !important;
		}
	}

	* {
		pointer-events: none;
	}
}
</style>
